import React, { useEffect, useMemo, useState } from "react";
import "./TenantNotConfiguredScreen.css";

const SUPPORT_EMAIL = "support@games3dcreations.com";
const CODE = "TC-001  tenant_not_configured";
const TYPE_MS = 28;
// phase: 0 idle, 1 headline, 2 node0 ok, 3 wire0, 4 node1 checking, 5 node1 ok,
// 6 wire1, 7 node2 checking, 8 node2 result, 9 actions
const TIMES = [0, 150, 700, 1050, 1600, 2100, 2450, 3000, 3700, 4000];
const FINAL_PHASE = TIMES.length - 1;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useTyped(text, runKey, reduce) {
  const [n, setN] = useState(reduce ? text.length : 0);
  useEffect(() => {
    if (reduce) {
      setN(text.length);
      return undefined;
    }
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const c = Math.min(text.length, Math.floor((now - t0) / TYPE_MS));
      setN(c);
      if (c < text.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, runKey, reduce]);
  return text.slice(0, n);
}

function RouteNode({ label, value, okLabel, failLabel, state, empty }) {
  const status =
    state === "checking" ? ["wait", "Checking"]
      : state === "ok" ? ["ok", okLabel]
        : state === "fail" ? ["fail", failLabel]
          : ["", "Waiting"];
  const on = state !== "idle";
  const cls = `tnc-node${on ? " on" : ""}${empty && state === "fail" ? " empty" : ""}`;
  return (
    <div className={cls}>
      <div className="tnc-nl">{label}</div>
      <div className="tnc-nv">{state === "fail" || !empty ? value : "…"}</div>
      <div className="tnc-ns">
        <span className={`tnc-st ${status[0]}`}><i />{status[1]}</span>
      </div>
    </div>
  );
}

/**
 * Shown when the current domain has no training center mapped to it.
 * Rendered outside ThemeProvider, so it is self-styled.
 */
export default function TenantNotConfiguredScreen() {
  const reduce = useMemo(prefersReducedMotion, []);
  const domain = window.location.hostname;
  const [phase, setPhase] = useState(reduce ? FINAL_PHASE : 0);
  const [run, setRun] = useState(0);
  const [copied, setCopied] = useState(false);
  const typed = useTyped(CODE, run, reduce);
  const ref = useMemo(
    () => `${new Date().toISOString().slice(0, 16).replace("T", " ")} UTC`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run],
  );

  useEffect(() => {
    if (reduce) {
      setPhase(FINAL_PHASE);
      return undefined;
    }
    setPhase(0);
    const timers = TIMES.map((t, i) => setTimeout(() => setPhase(i), t + 60));
    return () => timers.forEach(clearTimeout);
  }, [run, reduce]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const nodeState = [
    phase >= 2 ? "ok" : "idle",
    phase >= 5 ? "ok" : phase >= 4 ? "checking" : "idle",
    phase >= 8 ? "fail" : phase >= 7 ? "checking" : "idle",
  ];

  const copyDomain = async () => {
    try {
      await navigator.clipboard.writeText(domain);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    "Tenant not configured",
  )}&body=${encodeURIComponent(`Domain: ${domain}`)}`;

  return (
    <div className="tnc" data-testid="tenant-not-configured">
      <header className="tnc-top"><div className="tnc-wm">Responder Training Center</div></header>
      <main className="tnc-main">
        <div className="tnc-col" key={run}>
          <div className="tnc-code" aria-hidden="true">
            <span>{typed}</span>
            {phase < FINAL_PHASE && <i className="tnc-cur" />}
          </div>
          <h1 className={`tnc-big${phase >= 1 ? " in" : ""}`} aria-label="This domain isn’t set up yet.">
            <span className="tnc-ln"><span>This domain isn’t</span></span>
            <span className="tnc-ln"><span>set up yet.</span></span>
          </h1>
          <p className={`tnc-lead${phase >= 1 ? " in" : ""}`}>
            Sign-in will work once <b>{domain}</b> is linked to a training center.
            Contact support and include the domain.
          </p>
          <div className="tnc-route" aria-live="polite">
            <RouteNode label="Your browser" value={`${domain}/login`} okLabel="Sent" state={nodeState[0]} />
            <div className={`tnc-wire${phase >= 3 ? " go" : ""}`}><span className="tnc-fill" /><span className="tnc-pk" /></div>
            <RouteNode label="Domain" value="Resolved" okLabel="Found" state={nodeState[1]} />
            <div className={`tnc-wire${phase >= 6 ? " broken" : ""}`}><span className="tnc-fill" /><span className="tnc-pk" /></div>
            <RouteNode label="Training center" value="Not linked to this domain" failLabel="None" state={nodeState[2]} empty />
          </div>
          <div className={`tnc-acts${phase >= FINAL_PHASE ? " in" : ""}`}>
            <a className="tnc-btn" href={mailto}>Contact support</a>
            <button type="button" className="tnc-lnk" onClick={copyDomain}>Copy domain</button>
            {copied && <span className="tnc-toast" role="status">Copied to clipboard</span>}
            {!reduce && (
              <button type="button" className="tnc-replay" onClick={() => setRun((r) => r + 1)}>
                ↻ Run check again
              </button>
            )}
          </div>
        </div>
      </main>
      <footer className="tnc-foot">
        <span>{ref}</span>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </footer>
    </div>
  );
}
